import { keyOfChord, transposeChord, transposeKey, usesFlats } from "./chord";

/** A chord sheet read from ChordPro, ready to show. */
export type Sheet = {
    title?: string;
    artist?: string;
    key?: string;
    sections: SheetSection[];
};

/** A part of a song, like a verse or the chorus. */
export type SheetSection = {
    /** What ChordPro calls it, like `verse` or `chorus`, or `none` outside a section. */
    kind: string;
    label: string;
    lines: SheetLine[];
};

/**
 * A line of lyrics with chords, or a remark like "Capo 2". A music line whose chords can be moved knows which line
 * of the text it is, so arranging chords can change it there.
 */
export type SheetLine = { kind: "music"; segments: Segment[]; line?: number } | { kind: "comment"; text: string };

/** A chord with the lyrics it's played over. Either can be empty. */
export type Segment = {
    chord: string;
    lyrics: string;
};

/** A ChordPro directive, like `{title: Amazing Grace}`. */
type Directive = {
    name: string;
    value: string;
};

/** The full names of directives that are often written short. */
const ALIASES: Record<string, string> = {
    t: "title",
    st: "subtitle",
    c: "comment",
    ci: "comment_italic",
    cb: "comment_box",
    soc: "start_of_chorus",
    eoc: "end_of_chorus",
    sov: "start_of_verse",
    eov: "end_of_verse",
    sob: "start_of_bridge",
    eob: "end_of_bridge",
    sot: "start_of_tab",
    eot: "end_of_tab",
    sog: "start_of_grid",
    eog: "end_of_grid",
};

/** Directives that show their text as a remark on the sheet. */
const COMMENTS = new Set(["comment", "comment_italic", "comment_box", "highlight"]);

/** Labels for sections that don't have their own. */
const SECTION_LABELS: Record<string, string> = { verse: "Verse", chorus: "Chorus", bridge: "Bridge", tab: "Tab", grid: "Grid" };

/** Sections whose lines are shown as written, without looking for chords. */
const AS_WRITTEN = new Set(["tab", "grid"]);

/** Reads ChordPro text into a sheet, transposed by `transpose` half steps. */
export function readSheet(text: string, transpose = 0): Sheet {
    const sheet: Sheet = { sections: [] };
    let section: SheetSection | undefined;
    let explicit = false;
    let lastChorus: SheetLine[] | undefined;

    const current = () => {
        if (!section) {
            section = { kind: "none", label: "", lines: [] };
            sheet.sections.push(section);
        }

        return section;
    };

    for (const [index, line] of lines(transposeText(text, transpose)).entries()) {
        const directive = readDirective(line);

        if (directive) {
            const { name, value } = directive;

            if (name === "title" || name === "artist" || name === "key") {
                sheet[name] = value;
            } else if (COMMENTS.has(name)) {
                current().lines.push({ kind: "comment", text: value });
            } else if (name.startsWith("start_of_")) {
                const kind = name.slice("start_of_".length);

                section = { kind, label: value || SECTION_LABELS[kind] || kind, lines: [] };
                sheet.sections.push(section);
                explicit = true;
            } else if (name.startsWith("end_of_")) {
                if (section?.kind === "chorus") lastChorus = section.lines;

                section = undefined;
                explicit = false;
            } else if (name === "chorus") {
                // Plays the last chorus again. Without one, it's just a reminder on the sheet.
                if (lastChorus) sheet.sections.push({ kind: "chorus", label: value || "Chorus", lines: [...lastChorus] });
                else current().lines.push({ kind: "comment", text: value || "Chorus" });

                section = undefined;
            }

            continue;
        }

        if (line.trimStart().startsWith("#")) continue;

        // Outside a section, an empty line ends a paragraph. Inside one, it's only space.
        if (!line.trim()) {
            if (!explicit) section = undefined;

            continue;
        }

        const target = current();

        target.lines.push(
            AS_WRITTEN.has(target.kind)
                ? { kind: "music", segments: [{ chord: "", lyrics: line }] }
                : { kind: "music", segments: segmentsOf(line), line: index },
        );
    }

    sheet.sections = sheet.sections.filter((part) => part.lines.length > 0);

    return sheet;
}

/**
 * Transposes the chords and the `{key}` of ChordPro text, and changes nothing else in it.
 * Notes are spelled with flats or sharps to suit the new key.
 */
export function transposeText(text: string, steps: number): string {
    if (steps === 0) return text;

    const from = keyOf(text);
    const flats = usesFlats(from && transposeKey(from, steps));
    let asWritten = false;

    return text
        .split("\n")
        .map((line) => {
            const directive = readDirective(line);

            if (directive) {
                if (directive.name.startsWith("start_of_")) asWritten = AS_WRITTEN.has(directive.name.slice("start_of_".length));
                if (directive.name.startsWith("end_of_")) asWritten = false;

                return directive.name === "key" ? replaceValue(line, transposeKey(directive.value, steps)) : line;
            }

            if (asWritten || line.trimStart().startsWith("#")) return line;

            return line.replace(/\[([^\]]*)\]/g, (_, chord: string) => `[${transposeChord(chord, steps, flats)}]`);
        })
        .join("\n");
}

/** Writes the song's title and artist into ChordPro text, so the file makes sense in other apps too. Nothing else changes. */
export function writeDetails(text: string, details: { title: string; artist?: string }): string {
    const all = text.split("\n");
    const titleAt = setDirective(all, "title", details.title, 0);

    if (details.artist) setDirective(all, "artist", details.artist, titleAt + 1);

    return all.join("\n");
}

/** The lines of a text, without their line endings. */
function lines(text: string): string[] {
    return text.split(/\r?\n/);
}

function readDirective(line: string): Directive | undefined {
    const match = /^\s*\{\s*([a-zA-Z_]+)(?:\s*[:\s]\s*(.*?))?\s*\}\s*$/.exec(line);

    if (!match) return undefined;

    const name = match[1].toLowerCase();

    return { name: ALIASES[name] ?? name, value: match[2] ?? "" };
}

/** Replaces the value of a directive line, keeping how it's written. */
function replaceValue(line: string, value: string): string {
    return line.replace(
        /^(\s*\{\s*[a-zA-Z_]+\s*[:\s]\s*)(.*?)(\s*\}\s*)$/,
        (_, before: string, __: string, after: string) => before + value + after,
    );
}

/**
 * Sets a directive: changes its value where it already is, or adds it at `index`.
 * Returns where the directive is.
 */
function setDirective(all: string[], name: string, value: string, index: number): number {
    const at = all.findIndex((line) => readDirective(line)?.name === name);

    if (at >= 0) {
        all[at] = `{${name}: ${value}}${all[at].endsWith("\r") ? "\r" : ""}`;

        return at;
    }

    all.splice(index, 0, `{${name}: ${value}}`);

    return index;
}

/** The key of a song: its `{key}`, or else what its first chord suggests. */
function keyOf(text: string): string | undefined {
    for (const line of lines(text)) {
        const directive = readDirective(line);

        if (directive?.name === "key" && directive.value) return directive.value;
    }

    for (const line of lines(text)) {
        if (readDirective(line) || line.trimStart().startsWith("#")) continue;

        const chord = /\[([^\]]*)\]/.exec(line)?.[1];
        const key = chord && keyOfChord(chord);

        if (key) return key;
    }

    return undefined;
}

/**
 * Splits a line like `[G]Amazing [C]grace` into chords with the lyrics after them. Each word becomes its own
 * segment, so a long line can wrap between words without a chord losing its place.
 */
function segmentsOf(line: string): Segment[] {
    const parts = line.split(/\[([^\]]*)\]/);
    const segments: Segment[] = [];

    const add = (chord: string, lyrics: string) => {
        const words = lyrics === "" ? [""] : lyrics.split(/(?<=\s)(?=\S)/);

        words.forEach((word, index) => segments.push({ chord: index === 0 ? chord.trim() : "", lyrics: word }));
    };

    if (parts[0]) add("", parts[0]);

    for (let i = 1; i < parts.length; i += 2) add(parts[i], parts[i + 1] ?? "");

    return segments;
}
